import type {AnnotationMessage, AnnotationTool, NormPoint} from './protocol';
import {colorForUserId} from './protocol';
import type {VideoRenderBox} from './videoRenderBox';

export type StrokeRecord = {
    strokeId: string;
    userId: string;
    tool: AnnotationTool;
    color: string;
    width: number;
    opacity: number;
    points: NormPoint[];
    shape?: {kind: 'rectangle' | 'circle' | 'line' | 'diamond'; from: NormPoint; to: NormPoint};
    /** Text anchor in content space (see protocol `text` message). */
    text?: {text: string; x: number; y: number; fontSize: number};
};

function shiftNorm(p: NormPoint, pan: NormPoint): NormPoint {
    return {x: p.x + pan.x, y: p.y + pan.y};
}

/** Mutate stroke geometry by delta in stored content space. */
export function translateStrokeRecord(s: StrokeRecord, dx: number, dy: number): void {
    for (const p of s.points) {
        p.x += dx;
        p.y += dy;
    }
    if (s.shape) {
        s.shape.from.x += dx;
        s.shape.from.y += dy;
        s.shape.to.x += dx;
        s.shape.to.y += dy;
    }
    if (s.text) {
        s.text.x += dx;
        s.text.y += dy;
    }
}

export class AnnotationEngine {
    private strokes: StrokeRecord[] = [];
    private strokeIndex = new Map<string, number>();
    /** Session pan offset in normalized video space (applied when rendering). */
    private layerPan: NormPoint = {x: 0, y: 0};

    getLayerPan(): NormPoint {
        return this.layerPan;
    }

    clear(): void {
        this.strokes = [];
        this.strokeIndex.clear();
    }

    getStrokes(): readonly StrokeRecord[] {
        return this.strokes;
    }

    /** Last stroke id for user (for undo targeting). */
    lastStrokeIdForUser(userId: string): string | undefined {
        for (let i = this.strokes.length - 1; i >= 0; i--) {
            if (this.strokes[i].userId === userId) {
                return this.strokes[i].strokeId;
            }
        }
        return undefined;
    }

    removeStroke(strokeId: string): StrokeRecord | undefined {
        const idx = this.strokeIndex.get(strokeId);
        if (idx === undefined) {
            return undefined;
        }
        const [removed] = this.strokes.splice(idx, 1);
        this.rebuildIndex();
        return removed;
    }

    /** Live pan of one stroke during drag; only the stroke owner can move it. */
    tryMoveStrokeDelta(strokeId: string, dx: number, dy: number, actingUserId: string): boolean {
        const idx = this.strokeIndex.get(strokeId);
        if (idx === undefined) {
            return false;
        }
        const s = this.strokes[idx];
        if (!s || s.userId !== actingUserId) {
            return false;
        }
        translateStrokeRecord(s, dx, dy);
        return true;
    }

    apply(msg: AnnotationMessage): void {
        switch (msg.type) {
            case 'start': {
                const opacity =
                    msg.tool === 'highlighter'
                        ? msg.opacity ?? 0.35
                        : msg.tool === 'eraser'
                          ? 1
                          : 1;
                const rec: StrokeRecord = {
                    strokeId: msg.strokeId,
                    userId: msg.userId,
                    tool: msg.tool,
                    color: msg.color,
                    width: msg.width,
                    opacity,
                    points: [],
                };
                this.strokeIndex.set(msg.strokeId, this.strokes.length);
                this.strokes.push(rec);
                break;
            }
            case 'draw': {
                const idx = this.strokeIndex.get(msg.strokeId);
                if (idx === undefined) {
                    return;
                }
                const s = this.strokes[idx];
                if (!s || s.userId !== msg.userId) {
                    return;
                }
                s.points.push(...msg.points);
                s.tool = msg.tool;
                s.color = msg.color;
                s.width = msg.width;
                if (msg.opacity != null) {
                    s.opacity = msg.opacity;
                }
                break;
            }
            case 'end':
                break;
            case 'shape': {
                const opacity = msg.opacity ?? 1;
                const rec: StrokeRecord = {
                    strokeId: msg.strokeId,
                    userId: msg.userId,
                    tool: msg.tool,
                    color: msg.color,
                    width: msg.width,
                    opacity,
                    points: [],
                    shape: {kind: msg.tool, from: msg.from, to: msg.to},
                };
                this.strokeIndex.set(msg.strokeId, this.strokes.length);
                this.strokes.push(rec);
                break;
            }
            case 'text': {
                const rec: StrokeRecord = {
                    strokeId: msg.strokeId,
                    userId: msg.userId,
                    tool: 'text',
                    color: msg.color,
                    width: msg.fontSize,
                    opacity: 1,
                    points: [],
                    text: {
                        text: msg.text,
                        x: msg.x,
                        y: msg.y,
                        fontSize: msg.fontSize,
                    },
                };
                this.strokeIndex.set(msg.strokeId, this.strokes.length);
                this.strokes.push(rec);
                break;
            }
            case 'layerPan':
                this.layerPan = {x: msg.x, y: msg.y};
                break;
            case 'strokeMove':
                this.tryMoveStrokeDelta(msg.strokeId, msg.dx, msg.dy, msg.userId);
                break;
            case 'clear':
                if (msg.all) {
                    this.clear();
                    this.layerPan = {x: 0, y: 0};
                } else {
                    this.strokes = this.strokes.filter((s) => s.userId !== msg.userId);
                    this.rebuildIndex();
                }
                break;
            case 'undo': {
                const target =
                    msg.strokeId ?? this.lastStrokeIdForUser(msg.userId);
                if (target) {
                    this.removeStroke(target);
                }
                break;
            }
            case 'redo': {
                for (const m of msg.messages) {
                    this.apply(m);
                }
                break;
            }
            case 'sync':
                for (const m of msg.messages) {
                    this.apply(m);
                }
                break;
            default:
                break;
        }
    }

    private rebuildIndex(): void {
        this.strokeIndex.clear();
        this.strokes.forEach((s, i) => this.strokeIndex.set(s.strokeId, i));
    }
}

function dist2(ax: number, ay: number, bx: number, by: number): number {
    const dx = ax - bx;
    const dy = ay - by;
    return dx * dx + dy * dy;
}

/** Minimum distance from point to line segment (same coord space as segment endpoints). */
export function distancePointToSegment(
    px: number,
    py: number,
    x1: number,
    y1: number,
    x2: number,
    y2: number
): number {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len2 = dx * dx + dy * dy;
    if (len2 < 1e-18) {
        return Math.sqrt(dist2(px, py, x1, y1));
    }
    let t = ((px - x1) * dx + (py - y1) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    const qx = x1 + t * dx;
    const qy = y1 + t * dy;
    return Math.sqrt(dist2(px, py, qx, qy));
}

/**
 * Topmost stroke under point (content-space). Used for element eraser.
 * `hitBase` is a small normalized distance (~0.008–0.02); stroke width widens the hit region for ink.
 */
export function findTopStrokeIdAtContentPoint(
    strokes: readonly StrokeRecord[],
    contentPt: NormPoint,
    hitBase = 0.012
): string | undefined {
    const {x: px, y: py} = contentPt;
    for (let i = strokes.length - 1; i >= 0; i--) {
        const s = strokes[i];
        const wPad = Math.max(0.004, s.width * 0.0012);
        const tol = hitBase + wPad;

        if (s.text?.text) {
            const t = s.text;
            const fs = Math.max(10, Math.min(72, t.fontSize));
            const nh = fs * 0.0011;
            const nw = Math.max(nh * 2, t.text.length * fs * 0.00055);
            if (px >= t.x - tol && px <= t.x + nw + tol && py >= t.y - tol && py <= t.y + nh + tol) {
                return s.strokeId;
            }
            continue;
        }

        if (s.shape) {
            const {from: a, to: b, kind} = s.shape;
            const minx = Math.min(a.x, b.x);
            const maxx = Math.max(a.x, b.x);
            const miny = Math.min(a.y, b.y);
            const maxy = Math.max(a.y, b.y);
            const pad = tol * 1.25;
            if (kind === 'line') {
                if (distancePointToSegment(px, py, a.x, a.y, b.x, b.y) <= tol * 2.5) {
                    return s.strokeId;
                }
                continue;
            }
            if (kind === 'rectangle' || kind === 'diamond') {
                if (px >= minx - pad && px <= maxx + pad && py >= miny - pad && py <= maxy + pad) {
                    return s.strokeId;
                }
                continue;
            }
            if (kind === 'circle') {
                const cx = (minx + maxx) / 2;
                const cy = (miny + maxy) / 2;
                const rx = Math.abs(maxx - minx) / 2 + pad;
                const ry = Math.abs(maxy - miny) / 2 + pad;
                if (rx < 1e-9 || ry < 1e-9) {
                    continue;
                }
                const nx = (px - cx) / rx;
                const ny = (py - cy) / ry;
                if (nx * nx + ny * ny <= 1.05) {
                    return s.strokeId;
                }
                continue;
            }
        }

        const pts = s.points;
        if (pts.length === 0) {
            continue;
        }
        if (pts.length === 1) {
            if (Math.sqrt(dist2(px, py, pts[0].x, pts[0].y)) <= tol * 3) {
                return s.strokeId;
            }
            continue;
        }
        let best = Infinity;
        for (let j = 1; j < pts.length; j++) {
            const d = distancePointToSegment(px, py, pts[j - 1].x, pts[j - 1].y, pts[j].x, pts[j].y);
            if (d < best) {
                best = d;
            }
        }
        if (best <= tol * 2.2) {
            return s.strokeId;
        }
    }
    return undefined;
}

/** Canvas CSS size + mapping from normalized video coords to canvas pixels. */
export type AnnotationCanvasLayout = {
    canvasW: number;
    canvasH: number;
    map: VideoRenderBox;
};

function toCanvasX(xNorm: number, map: VideoRenderBox): number {
    return map.offsetX + xNorm * map.renderWidth;
}

function toCanvasY(yNorm: number, map: VideoRenderBox): number {
    return map.offsetY + yNorm * map.renderHeight;
}

export function renderStrokes(
    ctx: CanvasRenderingContext2D,
    strokes: readonly StrokeRecord[],
    layout: AnnotationCanvasLayout,
    options?: {highlightUserId?: string; debug?: boolean; layerPan?: NormPoint}
): void {
    const {canvasW, canvasH, map} = layout;
    const pan = options?.layerPan ?? {x: 0, y: 0};
    ctx.clearRect(0, 0, canvasW, canvasH);
    for (const s of strokes) {
        const displayColor =
            s.userId === options?.highlightUserId ? s.color : s.color || colorForUserId(s.userId);
        ctx.save();
        ctx.globalAlpha = s.opacity;
        ctx.lineWidth = Math.max(1, s.width);
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        if (s.tool === 'text' && s.text) {
            const a = shiftNorm({x: s.text.x, y: s.text.y}, pan);
            const cx = toCanvasX(a.x, map);
            const cy = toCanvasY(a.y, map);
            const fs = Math.max(10, Math.min(72, s.text.fontSize));
            ctx.font = `${fs}px ui-sans-serif, system-ui, -apple-system, sans-serif`;
            ctx.fillStyle = displayColor;
            ctx.textBaseline = 'top';
            ctx.textAlign = 'left';
            ctx.fillText(s.text.text, cx, cy);
            ctx.restore();
            continue;
        }
        if (s.shape) {
            ctx.strokeStyle = displayColor;
            ctx.fillStyle = displayColor;
            const f0 = shiftNorm(s.shape.from, pan);
            const f1 = shiftNorm(s.shape.to, pan);
            const x0 = toCanvasX(f0.x, map);
            const y0 = toCanvasY(f0.y, map);
            const x1 = toCanvasX(f1.x, map);
            const y1 = toCanvasY(f1.y, map);
            if (s.shape.kind === 'line') {
                ctx.beginPath();
                ctx.moveTo(x0, y0);
                ctx.lineTo(x1, y1);
                ctx.stroke();
            } else if (s.shape.kind === 'rectangle') {
                const left = Math.min(x0, x1);
                const top = Math.min(y0, y1);
                ctx.strokeRect(left, top, Math.abs(x1 - x0), Math.abs(y1 - y0));
            } else if (s.shape.kind === 'circle') {
                const cx = (x0 + x1) / 2;
                const cy = (y0 + y1) / 2;
                const rx = Math.abs(x1 - x0) / 2;
                const ry = Math.abs(y1 - y0) / 2;
                ctx.beginPath();
                ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
                ctx.stroke();
            } else if (s.shape.kind === 'diamond') {
                const left = Math.min(x0, x1);
                const top = Math.min(y0, y1);
                const rw = Math.abs(x1 - x0);
                const rh = Math.abs(y1 - y0);
                const cx = left + rw / 2;
                const cy = top + rh / 2;
                ctx.beginPath();
                ctx.moveTo(cx, top);
                ctx.lineTo(left + rw, cy);
                ctx.lineTo(cx, top + rh);
                ctx.lineTo(left, cy);
                ctx.closePath();
                ctx.stroke();
            }
            ctx.restore();
            continue;
        }

        if (s.tool === 'eraser') {
            ctx.globalCompositeOperation = 'destination-out';
            ctx.strokeStyle = 'rgba(0,0,0,1)';
        } else {
            ctx.globalCompositeOperation = 'source-over';
            ctx.strokeStyle = displayColor;
        }

        const pts = s.points;
        if (pts.length === 0) {
            ctx.restore();
            continue;
        }
        if (pts.length === 1) {
            ctx.beginPath();
            const q = shiftNorm(pts[0], pan);
            const x = toCanvasX(q.x, map);
            const y = toCanvasY(q.y, map);
            ctx.arc(x, y, Math.max(1, s.width / 2), 0, Math.PI * 2);
            ctx.fillStyle = s.tool === 'eraser' ? '#000' : displayColor;
            ctx.fill();
            ctx.restore();
            continue;
        }
        ctx.beginPath();
        const p0 = shiftNorm(pts[0], pan);
        ctx.moveTo(toCanvasX(p0.x, map), toCanvasY(p0.y, map));
        for (let i = 1; i < pts.length; i++) {
            const pi = shiftNorm(pts[i], pan);
            ctx.lineTo(toCanvasX(pi.x, map), toCanvasY(pi.y, map));
        }
        ctx.stroke();
        ctx.restore();
    }

    if (options?.debug) {
        ctx.save();
        ctx.strokeStyle = 'rgba(148, 163, 184, 0.14)';
        ctx.lineWidth = 1;
        ctx.strokeRect(map.offsetX, map.offsetY, map.renderWidth, map.renderHeight);
        ctx.restore();
    }
}

/** Preview shape while dragging (normalized coords). */
export function renderShapePreview(
    ctx: CanvasRenderingContext2D,
    tool: 'rectangle' | 'circle' | 'line' | 'diamond',
    color: string,
    width: number,
    opacity: number,
    from: NormPoint,
    to: NormPoint,
    map: VideoRenderBox,
    layerPan?: NormPoint
): void {
    const pan = layerPan ?? {x: 0, y: 0};
    const f = shiftNorm(from, pan);
    const t = shiftNorm(to, pan);
    ctx.save();
    ctx.globalAlpha = opacity;
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    const x0 = toCanvasX(f.x, map);
    const y0 = toCanvasY(f.y, map);
    const x1 = toCanvasX(t.x, map);
    const y1 = toCanvasY(t.y, map);
    ctx.beginPath();
    if (tool === 'line') {
        ctx.moveTo(x0, y0);
        ctx.lineTo(x1, y1);
    } else if (tool === 'rectangle') {
        const left = Math.min(x0, x1);
        const top = Math.min(y0, y1);
        ctx.rect(left, top, Math.abs(x1 - x0), Math.abs(y1 - y0));
    } else if (tool === 'diamond') {
        const left = Math.min(x0, x1);
        const top = Math.min(y0, y1);
        const rw = Math.abs(x1 - x0);
        const rh = Math.abs(y1 - y0);
        const cx = left + rw / 2;
        const cy = top + rh / 2;
        ctx.moveTo(cx, top);
        ctx.lineTo(left + rw, cy);
        ctx.lineTo(cx, top + rh);
        ctx.lineTo(left, cy);
        ctx.closePath();
    } else {
        const cx = (x0 + x1) / 2;
        const cy = (y0 + y1) / 2;
        const rx = Math.abs(x1 - x0) / 2;
        const ry = Math.abs(y1 - y0) / 2;
        ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    }
    ctx.stroke();
    ctx.restore();
}
